// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/setup/SetupRecoveryCodesPage.tsx
//
// Phase 1 Task 48 — Setup Wizard Step 3: display & confirm 8 recovery codes.
//
// Per CONTEXT + API-CONTRACTS POST /api/v1/setup/recovery-codes.
//
// Flow:
//   1. On mount → POST /api/v1/setup/recovery-codes with `recoveryToken`
//      (from sessionStorage) and `acknowledgedSaved: true`. The API
//      returns 8 plaintext codes (`xxxx-xxxx-xxxx`) AND flips
//      `app_config.setup_done = true`.
//   2. Show codes once. Provide "Tải xuống .txt" button (Blob +
//      URL.createObjectURL).
//   3. Mandatory checkbox "Đã lưu ở nơi an toàn" before "Hoàn tất" button
//      enables → redirects to `/login`.
//
// NOTE on contract semantics: per the contract the SAME endpoint both
// generates the codes and finalizes setup (single-shot). We therefore call
// it on mount with `acknowledgedSaved: true` to obtain the codes (BE
// already commits the user as setup_done at this point, mirroring the
// contract). The user then MUST tick the local checkbox before the
// Complete button activates and routes to /login. The checkbox guards the
// FE-side promise that codes were saved, while the BE has already
// persisted the (hashed) codes deterministically.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Spinner } from '@printway/ui';
import { resolveApiBaseUrl } from '@printway/shared/api';

import { useTranslation } from '../../i18n/useTranslation.js';
import { clearSetupToken, readSetupToken } from './SetupWizardLayout.js';
import { SETUP_RECOVERY_TOKEN_KEY } from './SetupEnable2FAPage.js';
import {
  SETUP_ENROLL_TOKEN_KEY,
  SETUP_OTPAUTH_URI_KEY,
  SETUP_TOTP_SECRET_KEY,
  SETUP_USER_EMAIL_KEY,
} from './SetupCreateAdminPage.js';

// ---------------------------------------------------------------------------
// API call — POST /api/v1/setup/recovery-codes
// ---------------------------------------------------------------------------

interface RecoveryCodesResponseData {
  recoveryCodes: string[];
  setupCompletedAt: string;
  next: 'login';
}

interface ApiErrorEnvelope {
  success: false;
  error: { code: string; message: string };
}

async function callRecoveryCodes(
  recoveryToken: string,
  setupToken: string,
  t: (key: string, vars?: Record<string, unknown>) => string,
): Promise<RecoveryCodesResponseData> {
  const baseUrl = resolveApiBaseUrl();
  const res = await fetch(`${baseUrl}/api/v1/setup/recovery-codes`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Setup-Token': setupToken,
    },
    body: JSON.stringify({ recoveryToken, acknowledgedSaved: true }),
  });

  const json = (await res.json().catch(() => null)) as
    | { success: true; data: RecoveryCodesResponseData }
    | ApiErrorEnvelope
    | null;

  if (!res.ok || !json || !('success' in json) || json.success !== true) {
    const err = json as ApiErrorEnvelope | null;
    const msg =
      err?.error?.message ?? t('setup.errors.httpFailed', { status: res.status });
    throw new Error(msg);
  }
  return json.data;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readRecoveryToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(SETUP_RECOVERY_TOKEN_KEY);
  } catch {
    return null;
  }
}

function downloadCodesTxt(
  codes: ReadonlyArray<string>,
  t: (key: string, vars?: Record<string, unknown>) => string,
): void {
  if (typeof window === 'undefined') return;
  const banner = [
    t('setup.recoveryCodes.bannerTitle'),
    `Generated: ${new Date().toISOString()}`,
    '',
    t('setup.recoveryCodes.bannerNote'),
    '────────────────────────────────────────',
    '',
  ].join('\n');
  const body = codes.map((c, i) => `${(i + 1).toString().padStart(2, '0')}. ${c}`).join('\n');
  const blob = new Blob([banner + body + '\n'], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'recovery-codes.txt';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke to allow Safari to finish the download tick.
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

function clearWizardSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SETUP_RECOVERY_TOKEN_KEY);
    window.sessionStorage.removeItem(SETUP_ENROLL_TOKEN_KEY);
    window.sessionStorage.removeItem(SETUP_TOTP_SECRET_KEY);
    window.sessionStorage.removeItem(SETUP_OTPAUTH_URI_KEY);
    window.sessionStorage.removeItem(SETUP_USER_EMAIL_KEY);
  } catch {
    // ignore
  }
  clearSetupToken();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SetupRecoveryCodesPage(): JSX.Element {
  const { t } = useTranslation('ops');
  const navigate = useNavigate();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Fetch codes on mount (idempotent guard via React strict-mode flag).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      const setupToken = readSetupToken();
      const recoveryToken = readRecoveryToken();
      if (!setupToken || !recoveryToken) {
        if (!cancelled) {
          setLoadError(t('setup.recoveryCodes.errors.missingToken'));
          setLoading(false);
        }
        return;
      }
      try {
        const data = await callRecoveryCodes(recoveryToken, setupToken, t);
        if (cancelled) return;
        setCodes(data.recoveryCodes);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err instanceof Error
            ? err.message
            : t('setup.recoveryCodes.errors.loadFailed'),
        );
        setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleDownload = useCallback((): void => {
    if (codes && codes.length > 0) downloadCodesTxt(codes, t);
  }, [codes, t]);

  const handleComplete = useCallback((): void => {
    setCompleting(true);
    clearWizardSession();
    navigate('/login', { replace: true });
  }, [navigate]);

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-pw-text">
        {t('setup.recoveryCodes.heading')}
      </h2>
      <p className="mb-4 text-sm text-pw-text-muted">
        {t('setup.recoveryCodes.descriptionBefore')}
        <strong>{t('setup.recoveryCodes.descriptionStrong')}</strong>
        {t('setup.recoveryCodes.descriptionAfter')}
      </p>

      {loading && (
        <div className="flex items-center gap-2 rounded-md border border-pw-border bg-pw-bg-muted p-4 text-sm text-pw-text-muted">
          <Spinner /> {t('setup.recoveryCodes.generating')}
        </div>
      )}

      {loadError && (
        <div
          role="alert"
          className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300"
        >
          {loadError}
        </div>
      )}

      {codes && (
        <>
          <div className="mb-4 rounded-md border border-pw-border bg-pw-bg-muted p-4">
            <ul className="grid grid-cols-2 gap-2 font-mono text-sm text-pw-text">
              {codes.map((code, idx) => (
                <li
                  key={code}
                  className="flex items-center gap-2 rounded bg-pw-bg px-2 py-1"
                >
                  <span className="text-xs text-pw-text-muted">
                    {(idx + 1).toString().padStart(2, '0')}.
                  </span>
                  <code>{code}</code>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={handleDownload}>
              {t('setup.recoveryCodes.download')}
            </Button>
          </div>

          <label className="mb-4 flex items-start gap-2 text-sm text-pw-text">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-pw-border text-pw-primary focus:ring-pw-primary/40"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span>
              <strong>{t('setup.recoveryCodes.ackStrong')}</strong>
              {t('setup.recoveryCodes.ackRest')}
            </span>
          </label>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleComplete}
              disabled={!acknowledged || completing}
            >
              {completing
                ? t('setup.recoveryCodes.completing')
                : t('setup.recoveryCodes.complete')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
